// components/modals/ChecklistModal.js
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const UserModal = ({ 
  isOpen, 
  onClose, 
  checklist, 
  onSave,
  mode // 'create' or 'edit'
}) => {
  const [formData, setFormData] = useState({
    titre: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialiser les données du formulaire
  useEffect(() => {
    if (checklist && mode === 'edit') {
      setFormData({
        titre: checklist?.titre || checklist?.name || '',
      });
    } else if (mode === 'create') {
      setFormData({
        titre: '',
      });
    }
    setErrors({});
  }, [checklist, mode, isOpen]);

  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};

    // Validation du titre
    if (!formData?.titre?.trim()) {
      newErrors.titre = 'Veuillez saisir un titre';
    } else if (formData.titre.trim().length < 2) {
      newErrors.titre = 'Le titre doit contenir au moins 2 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Gestion de la soumission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const checklistData = {
        ...formData,
        titre: formData.titre.trim(),
      };

      // Si c'est une édition, ajouter l'ID
      if (mode === 'edit' && checklist?.id) {
        checklistData.id = checklist.id;
      }

      // Appeler la fonction de sauvegarde
      await onSave(checklistData);
      
      onClose();
      
    } catch (err) {
      console.error('❌ Erreur lors de la sauvegarde:', err);
      setErrors({ submit: err.message || 'Erreur lors de la sauvegarde' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gestion des changements de champ
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Effacer l'erreur quand l'utilisateur commence à taper
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Gestion de la touche Entrée
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-300 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {mode === 'create' ? 'Nouveau point de contrôle' : 'Modifier le point de contrôle'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {mode === 'create' 
                ? 'Créer un nouveau point de contrôle' 
                : 'Modifier les informations du point de contrôle'
              }
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Message d'erreur global */}
            {errors.submit && (
              <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={16} className="mr-2" />
                  {errors.submit}
                </div>
              </div>
            )}

            {/* Champs du formulaire */}
            <div className="space-y-4">
              <div>
                <Input
                  type="text"
                  label="Titre du point de contrôle"
                  required
                  value={formData?.titre}
                  onChange={(e) => handleInputChange('titre', e?.target?.value)}
                  onKeyPress={handleKeyPress}
                  error={errors?.titre}
                  placeholder="Ex: Vérification des extincteurs"
                  disabled={isSubmitting}
                  autoFocus
                />
                {!errors?.titre && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Donnez un nom clair et descriptif à votre point de contrôle
                  </p>
                )}
              </div>

              {/* Informations supplémentaires (optionnel) */}
              {mode === 'edit' && checklist && (
                <div className="p-3 bg-muted/30 rounded-lg border border-border">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {checklist.code && (
                      <div>
                        <span className="text-muted-foreground">Code:</span>
                        <span className="ml-2 font-medium">{checklist.code}</span>
                      </div>
                    )}
                    {checklist.category && (
                      <div>
                        <span className="text-muted-foreground">Catégorie:</span>
                        <span className="ml-2 font-medium">{checklist.category}</span>
                      </div>
                    )}
                    {checklist.status && (
                      <div>
                        <span className="text-muted-foreground">Statut:</span>
                        <span className="ml-2 font-medium">{checklist.status}</span>
                      </div>
                    )}
                    {checklist.itemsCount !== undefined && (
                      <div>
                        <span className="text-muted-foreground">Éléments:</span>
                        <span className="ml-2 font-medium">{checklist.itemsCount}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Annuler
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            iconName="Save"
            iconPosition="left"
            iconSize={16}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <Icon name="Loader" size={16} className="animate-spin mr-2" />
                Enregistrement...
              </div>
            ) : (
              mode === 'create' ? 'Créer' : 'Sauvegarder'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserModal;